import type {ReactNode} from 'react'

import type {NewProjectAction} from './types'

interface NewProjectFormProps {
  /** Repo owner login (e.g. `monalisa`). */
  owner: string
  /** Repo name (e.g. `test-repo`). */
  repo: string
  /** Hidden-field bundle from the projects JSON response. */
  action: NewProjectAction
  /**
   * The visible submit element — typically a Primer `<Button type="submit">` for the
   * header CTA or `<Blankslate.PrimaryAction type="submit">` for the empty state.
   */
  children: ReactNode
}

/**
 * Renders the Rails `repos/memexes#upsert_repo_project` form with `action_type=create`
 * and `_method=put`, mirroring `Memex::ProjectList::AddOrLinkProjectButtonContainerComponent`.
 *
 * The server creates the project, links it to the repo, and redirects to the new project
 * URL — a true "hard navigation" CTA that requires no JavaScript at submit time. The
 * caller supplies the visible submit element so the same form can power both the header
 * CTA and the blankslate primary action.
 */
export function NewProjectForm({owner, repo, action: {csrfToken, defaultTitle}, children}: NewProjectFormProps) {
  const upsertPath = `/${owner}/${repo}/projects/beta/upsert`

  return (
    <form action={upsertPath} method="post" data-testid="new-project-form">
      <input type="hidden" name="_method" value="put" />
      {/* eslint-disable-next-line github/authenticity-token */}
      <input type="hidden" name="authenticity_token" value={csrfToken} />
      <input type="hidden" name="memex_project[title]" value={defaultTitle} />
      <input type="hidden" name="memex_project[is_template]" value="false" />
      <input type="hidden" name="action_type" value="create" />
      {children}
    </form>
  )
}
